import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
  useRef,
} from "react";
import { Descriptions, Table, App } from "antd";
import type { CustomerDTO } from "@ifa/utils";
import {
  createCustomer,
  COMMON_DICTS,
  useMetadataStore,
  useCountryStore,
  getMetadataLabel,
} from "@ifa/utils";
import { PersonForm } from "../../../../OrderForms/PersonForm";
import type { PersonFormRef } from "../../../../OrderForms/PersonForm";
import type { PolicyholderVO } from "../../../../OrderForms/orders";
import CustomCard from "../../../../../components/CustomCard";
import dayjs from "dayjs";

type CustomerProfileSectionProps = {
  customer: CustomerDTO | null;
  policyholder?: PolicyholderVO | null;
  mode: "view" | "edit";
  onDirtyChange?: (dirty: boolean) => void;
  onSave?: (data: PolicyholderVO) => void;
};

export type CustomerProfileHandle = {
  save: () => Promise<PolicyholderVO | undefined>;
  cancel: () => void;
  isDirty: () => boolean;
};

export const CustomerProfileSection = forwardRef<
  CustomerProfileHandle,
  CustomerProfileSectionProps
>(({ customer, policyholder, mode, onDirtyChange, onSave }, ref) => {
  const { message: antMessage } = App.useApp();
  const [editData, setEditData] = useState<PolicyholderVO | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [initialData, setInitialData] = useState<PolicyholderVO | null>(null);
  const hasFetchedCountries = useRef(false);
  const personFormRef = useRef<PersonFormRef>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { selectMetadataList } = useMetadataStore();
  const countryStore = useCountryStore();
  const { allCountries, loaded, fetchCountryList } = countryStore;

  // 数据规范化函数：确保数据结构符合 PersonForm 要求
  const normalizePersonData = useCallback((person: any): PolicyholderVO => {
    if (!person) return person;

    // Deep clone to prevent shared references
    const newPerson: any = {
      ...person,
      basic: person.basic ? { ...person.basic } : undefined,
      personal: person.personal ? { ...person.personal } : {},
      work: person.work
        ? {
            ...person.work,
            employer: person.work.employer
              ? { ...person.work.employer }
              : undefined,
          }
        : undefined,
      tax: person.tax ? { ...person.tax } : {},
      familyMembers: person.familyMembers
        ? [...person.familyMembers]
        : undefined,
    };

    // Ensure other object exists
    if (!newPerson.other) newPerson.other = {};

    // Migrate deprecated root-level fields to other object
    if (
      person.isVulnerableCustomer !== undefined &&
      newPerson.other.isVulnerableCustomer === undefined
    ) {
      newPerson.other.isVulnerableCustomer = person.isVulnerableCustomer;
    }
    if (
      person.isProfessionalInvestor !== undefined &&
      newPerson.other.isProfessionalInvestor === undefined
    ) {
      newPerson.other.isProfessionalInvestor = person.isProfessionalInvestor;
    }
    if (person.remark !== undefined && newPerson.other.remark === undefined) {
      newPerson.other.remark = person.remark;
    }

    // Set defaults for boolean fields
    if (newPerson.personal.smoke == null) newPerson.personal.smoke = false;
    if (newPerson.tax.hasOtherTaxResidency == null)
      newPerson.tax.hasOtherTaxResidency = false;
    if (newPerson.tax.isUSCitizen == null) newPerson.tax.isUSCitizen = false;
    if (newPerson.tax.hasHKIDCard == null) newPerson.tax.hasHKIDCard = false;
    if (newPerson.tax.isHKPermanentResident == null)
      newPerson.tax.isHKPermanentResident = false;
    if (newPerson.other.isVulnerableCustomer == null)
      newPerson.other.isVulnerableCustomer = false;
    if (newPerson.other.isProfessionalInvestor == null)
      newPerson.other.isProfessionalInvestor = false;

    return newPerson;
  }, []);

  useEffect(() => {
    // 使用 ref 防止 StrictMode 下的重复调用
    if (!loaded && !hasFetchedCountries.current) {
      hasFetchedCountries.current = true;
      fetchCountryList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 空依赖数组，只在挂载时执行一次

  const getLabel = (
    code: string | null,
    value: any,
    fallback?: { label: string; value: any }[],
  ) => {
    if (value === undefined || value === null || value === "") return "-";

    // Country special case
    if (code === "COUNTRY") {
      const c = allCountries.find((country) => country.countryCode === value);
      if (c) return c.cname || c.name || value;
    }

    if (code) {
      const meta = selectMetadataList.find((m) => m.code === code);
      if (meta?.items?.length) {
        const item = meta.items.find((i) => i.key === value);
        if (item) return item.name || item.key;
      }
    }

    if (fallback) {
      const found = fallback.find((opt) => opt.value === value);
      if (found) return found.label;
    }

    return value;
  };

  useEffect(() => {
    if (mode === "edit" && policyholder) {
      const normalizedData = normalizePersonData(policyholder);
      setEditData(normalizedData);
      setInitialData(JSON.parse(JSON.stringify(normalizedData)));
      setIsDirty(false);
    }
  }, [mode, policyholder, normalizePersonData]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const handleEditChange = useCallback((data: PolicyholderVO) => {
    setEditData(data);
    setIsDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!editData || !customer) {
      antMessage.error("資料不足，無法儲存");
      return undefined;
    }

    try {
      // 验证表单
      if (personFormRef.current) {
        await personFormRef.current.validate();
      }

      // 构建扁平结构的客户数据，符合新接口结构
      const payload = {
        customerId: customer.customerId,
        basic: editData.basic,
        personal: editData.personal,
        tax: editData.tax,
        work: editData.work,
        familyMembers: editData.familyMembers,
        other: editData.other, // 直接传递完整的 other 对象
      };

      await createCustomer(payload as any);
      antMessage.success("儲存成功");
      setIsDirty(false);
      onDirtyChange?.(false);
      onSave?.(editData);
      return editData;
    } catch (error: any) {
      // 处理表单验证错误
      if (error?.errorFields && error.errorFields.length > 0) {
        const firstError = error.errorFields[0];
        const errorMessage = firstError.errors?.[0] || "請完善必填項";
        antMessage.error(errorMessage);
        return undefined;
      }

      // API 错误由全局错误拦截器处理
      throw error;
    }
  }, [editData, customer, antMessage, onDirtyChange, onSave]);

  const handleCancel = useCallback(() => {
    if (initialData) {
      setEditData(JSON.parse(JSON.stringify(initialData)));
    }
    setIsDirty(false);
    onDirtyChange?.(false);
  }, [initialData, onDirtyChange]);

  useImperativeHandle(
    ref,
    () => ({
      save: handleSave,
      cancel: handleCancel,
      isDirty: () => isDirty,
    }),
    [handleSave, handleCancel, isDirty],
  );

  // 编辑模式
  if (mode === "edit") {
    if (!editData) return <div style={{ padding: 24 }}>載入中...</div>;

    return (
      <div
        ref={scrollContainerRef}
        style={{ height: "100%", overflowY: "auto", padding: "0 20px" }}
      >
        <PersonForm
          ref={personFormRef}
          title="投保人"
          data={editData}
          onChange={handleEditChange}
          existingPersons={[]}
          readOnly={false}
          scrollContainer={scrollContainerRef.current}
        />
      </div>
    );
  }

  // 查看模式
  if (!policyholder) {
    return (
      <div style={{ padding: 24, textAlign: "center", color: "#999" }}>
        暫無投保人資料
      </div>
    );
  }

  const personData = policyholder;

  const getPersonSchema = (personData: any) => [
    {
      label: "身份證明文件類型",
      key: "basic.idType",
      render: (v: any) => getLabel("ID_TYPE", v, COMMON_DICTS.idType),
    },
    { label: "身份證明文件號碼", key: "basic.idNumber" },
    {
      label: "姓名(中文)",
      key: "fullChineseName",
      render: () =>
        `${personData?.basic?.chineseSurname || ""}${personData?.basic?.chineseGivenName || ""}`,
    },
    { label: "名字(中文)", key: "basic.chineseGivenName" },
    { label: "姓氏(英文)", key: "basic.englishSurname" },
    { label: "名字(英文)", key: "basic.englishGivenName" },
    {
      label: "出生日期",
      key: "basic.birthDate",
      render: (v: any) => (v ? dayjs(v).format("YYYY-MM-DD") : "-"),
    },
    {
      label: "年齡",
      key: "age",
      render: () => {
        if (!personData?.basic?.birthDate) return "-";
        return dayjs().diff(dayjs(personData.basic.birthDate), "year");
      },
    },
    {
      label: "性別",
      key: "basic.gender",
      render: (v: any) => getLabel("GENDER", v, COMMON_DICTS.gender),
    },
    {
      label: "稱謂",
      key: "basic.salutation",
      render: (v: any) => getLabel("SALUTATION", v, COMMON_DICTS.salutation),
    },
  ];

  const contactSchema = [
    {
      label: "手提電話",
      key: "personal.phone",
      render: (v: any) =>
        v ? `+${personData?.personal?.phoneAreaCode || ""} ${v}` : "-",
    },
    { label: "電郵", key: "personal.email" },
    {
      label: "通訊地址-國家/地區",
      key: "personal.country",
      render: (v: any) => getLabel("COUNTRY", v, COMMON_DICTS.country),
    },
    {
      label: "完整通訊地址",
      key: "personal.address",
      render: (v: any) => v || "-",
    },
    { label: "身高(CM)", key: "personal.height" },
    { label: "體重(KG)", key: "personal.weight" },
    {
      label: "是否吸煙",
      key: "personal.smoke",
      render: (v: any) => getLabel("SMOKE", v),
    },
    {
      label: "教育程度",
      key: "personal.education",
      render: (v: any) => getLabel("EDUCATION", v, COMMON_DICTS.education),
    },
    {
      label: "婚姻狀況",
      key: "personal.marriage",
      render: (v: any) =>
        getLabel("MARRIAGE_STATUS", v, COMMON_DICTS.maritalStatus),
    },
  ];

  const workSchema = [
    {
      label: "就業類型",
      key: "work.type",
      render: (v: any) =>
        getLabel("WORK_INFO_EMPLOYMENT_TYPE", v, COMMON_DICTS.jobType),
    },
    { label: "僱主(公司)名稱", key: "work.employer.name" },
    { label: "僱主(公司)地址", key: "work.employer.address" },
    {
      label: "業務性質(行業)",
      key: "work.employer.industry",
      render: (v: any) =>
        getLabel("WORK_INFO_EMPLOYER_INDUSTRY", v, COMMON_DICTS.industry),
    },
    { label: "職位及日常職務", key: "work.jobTitle" },
    {
      label: "預計退休年齡",
      key: "work.intendedRetirementAge",
      render: (v: any) => getLabel("INTENDED_RETIREMENT_AGE_OPTION", v, []),
    },
  ];

  const getTaxSchema = (personData: any) => {
    const baseSchema = [
      {
        label: "國籍(國家/地區)",
        key: "tax.nationality",
        render: (v: any) => getLabel("COUNTRY", v, COMMON_DICTS.country),
      },
      {
        label: "出生國家",
        key: "tax.countryOfBirth",
        render: (v: any) => getLabel("COUNTRY", v, COMMON_DICTS.country),
      },
      {
        label: "入境香港證件類型",
        key: "tax.hkEntryIdType",
        render: (v: any) =>
          getLabel("HK_ENTRY_ID_TYPE", v, COMMON_DICTS.hkEntryIdType),
      },
      { label: "入境香港證件號碼", key: "tax.hkEntryIdNumber" },
      {
        label: "是否有其他稅務居民身份",
        key: "tax.hasOtherTaxResidency",
        render: (v: any) => (v ? "是" : "否"),
      },
    ];

    // If hasOtherTaxResidency is true, add tax country and tax code fields
    if (personData?.tax?.hasOtherTaxResidency) {
      baseSchema.push(
        {
          label: "稅務國家",
          key: "tax.taxCountry",
          render: (v: any) => getLabel("COUNTRY", v, COMMON_DICTS.country),
        },
        {
          label: "稅務編號",
          key: "tax.taxCode",
        },
      );
    }

    baseSchema.push(
      {
        label: "是否擁有美國公民身份",
        key: "tax.isUSCitizen",
        render: (v: any) => getMetadataLabel("WHETHER", v, null),
      },
      {
        label: "是否有香港身份證",
        key: "tax.hasHKIDCard",
        render: (v: any) => getMetadataLabel("WHETHER", v, null),
      },
      {
        label: "是否是香港永久居民",
        key: "tax.isHKPermanentResident",
        render: (v: any) => getMetadataLabel("WHETHER", v, null),
      },
    );

    return baseSchema;
  };

  const renderDescriptions = (
    data: any,
    schema: {
      label: string;
      key: string;
      render?: (val: any) => React.ReactNode;
    }[],
  ) => {
    if (!data) return <span style={{ color: "#999" }}>無資料</span>;
    return (
      <Descriptions column={2} colon={false} layout="vertical">
        {schema.map((item) => {
          const val = item.key
            .split(".")
            .reduce((o, i) => (o ? o[i] : null), data);
          return (
            <Descriptions.Item
              key={item.key}
              label={<span style={{ color: "#999" }}>{item.label}</span>}
            >
              <span style={{ color: "#333", fontWeight: 500 }}>
                {item.render ? item.render(val) : val || "-"}
              </span>
            </Descriptions.Item>
          );
        })}
      </Descriptions>
    );
  };

  return (
    <div style={{ height: "100%", overflowY: "auto", padding: "0 20px" }}>
      <CustomCard
        title="01 客戶基本資料"
        bordered={false}
        style={{ marginBottom: 16 }}
      >
        {renderDescriptions(personData, getPersonSchema(personData))}
      </CustomCard>

      <CustomCard
        title="02 客戶個人資料"
        bordered={false}
        style={{ marginBottom: 16 }}
      >
        {renderDescriptions(personData, contactSchema)}
      </CustomCard>

      <CustomCard
        title="03 客戶工作資料"
        bordered={false}
        style={{ marginBottom: 16 }}
      >
        {renderDescriptions(personData, workSchema)}
      </CustomCard>

      <CustomCard
        title="04 客戶國籍、稅務等資料"
        bordered={false}
        style={{ marginBottom: 16 }}
      >
        {renderDescriptions(personData, getTaxSchema(personData))}
      </CustomCard>

      {personData.familyMembers && personData.familyMembers.length > 0 && (
        <CustomCard
          title="05 客戶家庭成員"
          bordered={false}
          style={{ marginBottom: 16 }}
        >
          <Table
            dataSource={personData.familyMembers}
            pagination={false}
            rowKey="id"
            columns={[
              {
                title: "成員",
                dataIndex: "name",
                key: "name",
                render: (_, __, idx) => `成員${idx + 1}`,
              },
              { title: "成員年齡", dataIndex: "age", key: "age" },
              {
                title: "成員關係",
                dataIndex: "relationship",
                key: "relationship",
                render: (v) =>
                  getLabel(
                    "FAMILY_MEMBER_RELATIONSHIP",
                    v,
                    COMMON_DICTS.relationship,
                  ),
              },
              {
                title: "是否需要供養",
                dataIndex: "isDependent",
                key: "isDependent",
                render: (v: any) => getMetadataLabel("WHETHER", v, null),
              },
            ]}
          />
        </CustomCard>
      )}

      <CustomCard
        title="06 其他資料"
        bordered={false}
        style={{ marginBottom: 16 }}
      >
        <Descriptions column={2} layout="vertical">
          <Descriptions.Item
            label={<span style={{ color: "#999" }}>是否重點關注客戶</span>}
          >
            <span style={{ color: "#333", fontWeight: 500 }}>
              {personData?.other?.isVulnerableCustomer ? "是" : "否"}
            </span>
          </Descriptions.Item>
          <Descriptions.Item
            label={<span style={{ color: "#999" }}>是否專業投資者/PI</span>}
          >
            <span style={{ color: "#333", fontWeight: 500 }}>
              {personData.other?.isProfessionalInvestor ? "是" : "否"}
            </span>
          </Descriptions.Item>
          {personData.other?.isVulnerableCustomer && (
            <>
              <Descriptions.Item
                label={<span style={{ color: "#999" }}>見證人姓名</span>}
              >
                <span style={{ color: "#333", fontWeight: 500 }}>
                  {personData.other.witnessName}
                </span>
              </Descriptions.Item>
              <Descriptions.Item
                label={<span style={{ color: "#999" }}>見證人證件類型</span>}
              >
                <span style={{ color: "#333", fontWeight: 500 }}>
                  {getLabel(
                    "ID_TYPE",
                    personData.other.witnessIdType,
                    COMMON_DICTS.idType,
                  )}
                </span>
              </Descriptions.Item>
              <Descriptions.Item
                label={<span style={{ color: "#999" }}>見證人證件號碼</span>}
                span={2}
              >
                <span style={{ color: "#333", fontWeight: 500 }}>
                  {personData.other.witnessIdNumber || "-"}
                </span>
              </Descriptions.Item>
            </>
          )}
          <Descriptions.Item
            label={<span style={{ color: "#999" }}>備註/其他說明</span>}
            span={2}
          >
            <span style={{ color: "#333", fontWeight: 500 }}>
              {personData.other?.remark ?? (personData as any).remark ?? "-"}
            </span>
          </Descriptions.Item>
        </Descriptions>
      </CustomCard>
    </div>
  );
});
